import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Paper,
  Grid,
  Radio,
  FormControlLabel,
  RadioGroup,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useSettings } from '@/contexts/SettingsContext';
import { ThemeMode } from '@/themes/themes';
import { Locale } from '@/contexts/SettingsContext';
import { useTranslation } from 'react-i18next';

interface SettingsDrawerProps {
  open: boolean;
  onClose: () => void;
}

export default function SettingsDrawer({ open, onClose }: SettingsDrawerProps) {
  const { themeMode, locale, setThemeMode, setLocale } = useSettings();
  const { t } = useTranslation();

  const themes: { value: ThemeMode; label: string; color: string }[] = [
    { value: 'light', label: t('settings.light'), color: '#ffffff' },
    { value: 'dark', label: t('settings.dark'), color: '#1e1e1e' },
    { value: 'blue', label: t('settings.blue'), color: '#0d47a1' },
    { value: 'green', label: t('settings.green'), color: '#2e7d32' },
    { value: 'purple', label: t('settings.purple'), color: '#6a1b9a' },
  ];

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { width: 320, p: 3 },
      }}
    >
      <Box>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            {t('settings.title')}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {/* Language Selection */}
        <Stack spacing={3}>
          <Box>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              {t('settings.language')}
            </Typography>
            <FormControl fullWidth sx={{ mt: 1 }}>
              <Select
                value={locale}
                onChange={(e) => setLocale(e.target.value as Locale)}
                size="small"
              >
                <MenuItem value="en">{t('settings.english')}</MenuItem>
                <MenuItem value="zh">{t('settings.chinese')}</MenuItem>
                <MenuItem value="de">{t('settings.german')}</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Divider />

          {/* Theme Selection */}
          <Box>
            <Typography variant="subtitle2" fontWeight="600" gutterBottom>
              {t('settings.theme')}
            </Typography>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {themes.map((theme) => (
                <Grid item xs={12} key={theme.value}>
                  <Paper
                    elevation={themeMode === theme.value ? 3 : 1}
                    sx={{
                      p: 2,
                      cursor: 'pointer',
                      border: themeMode === theme.value ? '2px solid' : '1px solid',
                      borderColor: themeMode === theme.value ? 'primary.main' : 'divider',
                      transition: 'all 0.2s',
                      '&:hover': {
                        borderColor: 'primary.main',
                        elevation: 2,
                      },
                    }}
                    onClick={() => setThemeMode(theme.value)}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: theme.color,
                          border: '1px solid',
                          borderColor: 'divider',
                        }}
                      />
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight="500">
                          {theme.label}
                        </Typography>
                      </Box>
                      <Radio checked={themeMode === theme.value} />
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </Box>
        </Stack>
      </Box>
    </Drawer>
  );
}
